import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { push } from 'react-router-redux';
import { ssgFormsAdapters } from '../common';
import * as demographicsActions from './demographics.actions';
import * as programActions from '../programs/program.actions';
import * as programUtilities from '../common/program-utilities';
import { LoadingPane, SsgFormComponent, ConfirmModalWindow, ssgFormActions, Widget } from '@social-solutions/ssg-forms';
import { adapters, formDefinition } from '@social-solutions/ssg-forms-processing';
import { toastr } from 'react-redux-toastr';
import './demographics.scss';

const FORM_COMPONENT_ID = "edit-demographics";

export class DemographicsComponent extends Component {
  constructor() {
    super();
    this.state = {
      localState: null, // hold the state needed to call save
      localModal: null,  // hold a reference to the confirmation modal
      updating: false,
    }
  }

  componentWillMount() {
    const { demographicsActions, ssgFormActions, programActions } = this.props;
    ssgFormActions.hardReset(FORM_COMPONENT_ID);
    programActions.fetchAllPrograms();
    demographicsActions.getSettings();
    demographicsActions.getSite()
    .then( data => {
      var value = data && data.value;
      demographicsActions.getForm(value);
      demographicsActions.getFormAnswers(value);
    });
  }

  /**
   * Downloads an attachment from the existing record
   * @param {string} formId
   * @param {string} elementId
   * @param {string} fileId
   */
  handleAttachmentDownload(formId, elementId, fileId) {
    const { demographicsActions } = this.props;
    return demographicsActions.fetchAttachment(fileId)
      .catch(error => {
        toastr.error("Failure", "Failed to download the attachment.");
      });
  }

  /*
   * Clear out any in-flight edits by doing a hard reset.
   */
  handleCancelClicked() {
    const { ssgFormActions } = this.props;
    ssgFormActions.resetForm(FORM_COMPONENT_ID);
    ssgFormActions.processForm(FORM_COMPONENT_ID);
    this.setState({updating: false});
  }

  /**
   * Handles the save button being clicked.
   * Will redirect user to first error in the form if validation fails.
   * Pops up confirm modal if validation passes.
   * @param {*} state
   */
  handleSaveClicked(state) {
    const { localModal } = this.state;

    const validationResult = state && state.validationResult;
    if(!!validationResult && !validationResult.passedValidation) {
      const firstError = state.validationResult.errors.first();
      const errorElement = firstError && document.getElementById(firstError.sourceId);
      errorElement && errorElement.focus();
      return;
    }

    this.setState({localState : state});
    localModal && localModal.show();
  }

  handleConfirmPopupWindowChoice(choice, state) {
    if (choice) {
      this.handleSave(state);
    }
    else {
      this.setState({localState : null});
    }
  }

  /**
   * Comb the demographic answers for the currently saved programs.
   * @returns {Array} Array of guids, representing program ids. Will be empty if no data is available.
   */
  getCurrentlySavedPrograms() {
    const { demographics } = this.props;
    const { answersState } = demographics || {};
    const { data } = answersState || {};
    const { programs } = data || {};
    if (!!programs && programs.length > 0) {
      return programs.map(progInfo => progInfo.programId);
    }

    return [];
  }

  /**
   * Handles updating demographics when popup has been confirmed.
   * @param {*} state
   */
  handleSave(state) {
    const { demographics, demographicsActions, ssgFormActions } = this.props;
    const { siteState } = demographics;
    const { data : site } = siteState;
    const siteId = site && site.value;
    const { updateDemographics, getFormAnswers, getForm,  } = demographicsActions;

    updateDemographics(siteId, state.value)
      .then(val => {
        ssgFormActions.resetForm(FORM_COMPONENT_ID);
        ssgFormActions.processForm(FORM_COMPONENT_ID);
        toastr.success('Success', 'Personal information saved.');
        this.setState({updating: false});
        getForm(siteId);
        getFormAnswers(siteId);
      })
      .catch(err => {
        if (!err.validationResult || err.validationResult.isValid)
          toastr.error(err.errorCode, err.errorMessage);
      });
  }

  /**
   * Handles the update button being clicked, enters edit mode
   */
  handleUpdate() {
    this.setState({updating: true});
  }

  render() {
    const { demographics, programs } = this.props;
    const { updating } = this.state;
    const { siteState, formState, answersState, settingsState, submitAnswersState } = demographics;
    const { data : form } = formState;
    const { data : answers } = answersState;
    const { data : settings } = settingsState;
    const { isFetching : isSubmitting } = submitAnswersState;
    const { demographicsEnabled } = settings || { demographicsEnabled : false };
    const fieldFormatSetting = (!!form && form.fieldFormatSetting) || 'US';

    const allProgs = programUtilities.getAllPrograms(programs);
    const currentlySavedProgs = this.getCurrentlySavedPrograms();

    // should never have been able to navigate here
    if(!demographicsEnabled) {
      return null;
    }

    const viewButtons = !form || form.elements.every(val => val.readOnly || val.hidden || val.disabled || val.isUnsupported || !formDefinition.elementStoresData(val.type)) ?
    [] :
    [{
      id: "update-tier-one-button",
      type: "button",
      display: "Update",
      ariaLabel: "Update",
      buttonType: "secondary",
      functionType: "none",
      onClick: () => this.handleUpdate(),
    }];

    const editButtons = [
      {
        id: 'cancel',
        display: 'CANCEL',
        buttonType: 'default',
        functionType: 'none',
        onClick: () => this.handleCancelClicked(),
      },
      {
        id: 'save',
        display: 'SAVE',
        buttonType: 'primary',
        functionType: 'submit',
        spinnerOn: isSubmitting,
        onClick: (state) => this.handleSaveClicked(state),
      }
    ];

    const demographicAnswers = form && answers && adapters.convertJsonResponse(form, answers);

    const formProps = {
      id: FORM_COMPONENT_ID,
      country: fieldFormatSetting,
      form,
      disableValidationResultList: true,
      readOnly: !updating,
      initialValues: demographicAnswers,
      buttonLayout: updating ? editButtons : viewButtons,
      onDownloadAttachment: (formId, elementId, fileId) => this.handleAttachmentDownload(formId, elementId, fileId),
      allPrograms: allProgs,
      initialSelectedPrograms: currentlySavedProgs,
      css: {
        title: "ssg-forms-text large account-widget-title",
        actions: "ssg-forms-plain-actions"
      }
    };

    return (
      <Widget css={{widgetDisplay: 'ssg-forms-widget account-widget demographics-form'}}>
        <LoadingPane
          loadingState={[
            ssgFormsAdapters.convertLoadingState(programs && programs.allPrograms),
            ssgFormsAdapters.convertLoadingState(siteState),
            ssgFormsAdapters.convertLoadingState(formState),
            ssgFormsAdapters.convertLoadingState(answersState),
            ssgFormsAdapters.convertLoadingState(settingsState),
          ]}
          loadingMessage={'Loading Personal Information...'}>
          <div className="acct validated-section">
            <div>
              <ConfirmModalWindow id={`${FORM_COMPONENT_ID}-confirm`}
                title={"Save Updates to Personal Info"}
                modalRef={(modal) => this.setState( {localModal : modal})}
                onClose={(e, confirmed) => this.handleConfirmPopupWindowChoice(confirmed, this.state.localState)}>
                <p>{'Are you ready to submit changes to your personal info?'}</p>
              </ConfirmModalWindow>
              <SsgFormComponent {...formProps}/>
            </div>
          </div>
        </LoadingPane>
      </Widget>
    );
  }
}

DemographicsComponent.propTypes = {
  demographics: PropTypes.object.isRequired,
  demographicsActions: PropTypes.any.isRequired,
  programActions: PropTypes.any.isRequired
};

const mapStateToProps = ({ demographics, programs }) => {
  return {
    demographics,
    programs
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    demographicsActions: bindActionCreators(demographicsActions, dispatch),
    redirectTo: bindActionCreators(push, dispatch),
    ssgFormActions: bindActionCreators(ssgFormActions, dispatch),
    programActions: bindActionCreators(programActions, dispatch)
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(DemographicsComponent);

/**
 * @typedef {Object} JsonResponseValue
 * @property {string} id
 * @property {*} value
 */

/**
 * @typedef {Object} JsonResponse
 * @property {string} id
 * @property {string} formId
 * @property {string} name
 * @property {string} heading
 * @property {JsonResponseValue[]} elements
 */

/**
 * @typedef {Object} JsonForm
 * @property {string} id
 * @property {string} name
 * @property {string} heading
 * @property {FormElement[]} elements
 * @property {FormRule[]} rules
 * @property {number?} maxTotalAttachmentSize
 * @property {boolean?} includeRecaptcha
 * @property {string?} programAssignmentType
 */



// WEBPACK FOOTER //
// src/demographics/demographics.component.js